import * as React from 'react';
import { useState, useMemo } from 'react';

import ThreeDotMenu, { ThreeDotMenuItem } from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import { useAppContext } from '@app/react-common/AppContext';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import Button from '@app/react-common/components/button/Button';
import {
  PrioritizationModal,
  PrioritizableItem,
} from '@app/react-common/components/prioritization-modal/PrioritizationModal';
import { useExtensionGroups } from '../context/ExtensionGroupsContext';
import { IGroupComponent } from '../../models/extension-group.models';
import {
  EXTENSION_TYPES_SUPPORTED_IN_GROUP_TYPE,
  ExtensionComponentType,
  ExtensionsGroupType,
  ExtensionsTypeToDisplayName,
  CheckoutExtensionCommonConfig,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ToggleButton } from '@app/react-common/components/toggle-button/ToggleButton';
import { ExpandableText } from '@app/react-common/components/expandable-text/ExpandableText';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { formatExtensionConditions } from '@app/sharedModule/components/rules-dynamic-form/rule-format';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';

const MAX_EXTENSIONS_PER_GROUP = 10;

const LINKED_EXTENSION_BANNER_MESSAGE =
  'This extension is set up to appear inside a shipping method and functions only as a standalone block on Shopify.';

interface ExtensionPrioritizableItem extends PrioritizableItem {
  type?: string;
  originalComponent: IGroupComponent;
}

export const ExtensionsGroupComponentList = () => {
  const { selectedGroup, availableExtensions, extensionActions } = useExtensionGroups();
  const [removeComponent, setRemoveComponent] = useState<IGroupComponent | null>(null);
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);

  const { emitAngularAction, showToast } = useAppContext();

  const groupComponents = selectedGroup?.groupComponents || [];

  const sortedGroupComponents = useMemo(() => {
    return [...groupComponents].sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [groupComponents]);

  const supportedExtensionTypes =
    EXTENSION_TYPES_SUPPORTED_IN_GROUP_TYPE[selectedGroup?.type ?? ExtensionsGroupType.EXTENSIONS];

  const extensionsNotInGroup = useMemo(() => {
    return availableExtensions.filter(
      (ext) =>
        !groupComponents.some((gc) => gc.extensionComponentId === ext.id) &&
        supportedExtensionTypes.includes(ext.type as ExtensionComponentType),
    );
  }, [availableExtensions, groupComponents, supportedExtensionTypes]);

  const extensionOptions: Option[] = useMemo(() => {
    return extensionsNotInGroup.map((ext) => {
      const name = ext.template?.internalName || ext.template?.title || 'Unnamed Extension';
      const type = ExtensionsTypeToDisplayName[ext.type] || ext.type || '';
      return {
        label: type ? `${name} (${type})` : name,
        value: ext.id,
      };
    });
  }, [extensionsNotInGroup]);

  const getComponentName = (component: IGroupComponent): string => {
    return component.extensionComponent?.template?.title || component.extensionComponent?.type || '-';
  };

  const handleRemoveComponent = async (component: IGroupComponent) => {
    if (selectedGroup?.id) {
      await extensionActions.removeExtensionFromGroup(selectedGroup.id, component.id);
    }
    setRemoveComponent(null);
  };

  const handleAddExtension = async (option: Option | null) => {
    if (option && selectedGroup?.id) {
      if (groupComponents.length >= MAX_EXTENSIONS_PER_GROUP) {
        showToast({
          message: `You can only add up to ${MAX_EXTENSIONS_PER_GROUP} extensions per group.`,
          mode: 'warning',
        });
        return;
      }

      const extension = availableExtensions.find((ext) => ext.id === option.value);
      const isLinked = extension?.template?.placementMode === CheckoutExtensionCommonConfig.PlacementMode.LINKED;

      if (isLinked) {
        showToast({
          message: LINKED_EXTENSION_BANNER_MESSAGE,
          mode: 'warning',
        });
        return;
      }

      await extensionActions.addExtensionToGroup(selectedGroup.id, option.value as number, extension?.enabled ?? false);
    }
  };

  const prioritizableItems: ExtensionPrioritizableItem[] = useMemo(() => {
    return [...groupComponents]
      .sort((a, b) => (a.priority || 0) - (b.priority || 0))
      .map((component) => ({
        id: component.id,
        name: component.extensionComponent?.template?.title || component.extensionComponent?.type || 'Unnamed',
        priority: component.priority,
        type:
          ExtensionsTypeToDisplayName[component.extensionComponent?.type || ''] || component.extensionComponent?.type,
        originalComponent: component,
      }));
  }, [groupComponents]);

  const handleSavePriorities = async (orderedItems: ExtensionPrioritizableItem[]) => {
    if (!selectedGroup?.id) return;

    const priorities = orderedItems.map((item, index) => ({
      id: item.id,
      priority: index,
    }));

    await extensionActions.updateComponentPriorities(selectedGroup.id, priorities);
  };

  const handleToggleComponent = async (component: IGroupComponent, enabled: boolean) => {
    if (!selectedGroup?.id) return;
    await extensionActions.toggleExtensionGroupComponent(selectedGroup.id, component.id, enabled);
  };

  const getComponentActions = (component: IGroupComponent): ThreeDotMenuItem[] => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          if (component.extensionComponentId && selectedGroup?.id) {
            emitAngularAction({
              action: 'edit-extension',
              payload: {
                extensionId: component.extensionComponentId,
                groupId: selectedGroup.id,
                extensionType: component.extensionComponent?.type,
                usageToken: component.extensionComponent?.usageToken,
              },
              callback: () => {},
            });
          }
        },
      },
      {
        label: 'Remove',
        onClick: () => {
          setRemoveComponent(component);
        },
      },
    ];
  };

  if (!selectedGroup) {
    return (
      <div className="extension-group-components-list">
        <div className="extension-group-components-list__empty">
          <p>No extension group selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="extension-group-components-list">
      <div className="extension-group-components-list__header">
        <Tooltip tooltipText="In order for an extension to be displayed, it still needs to be turned on in the Extensions tab">
          <h2>Extensions</h2>
          <i className="extension-group-components-list__info-icon">i</i>
        </Tooltip>
        <div className="extension-group-components-list__add-dropdown">
          <Button
            onClick={() => setIsPriorityModalOpen(true)}
            text="Extensions Order"
            size="medium"
            variant="secondary"
            disabled={groupComponents.length < 2}
          />
          <Dropdown
            options={extensionOptions}
            placeholder="+ Add Extension"
            onSelect={handleAddExtension}
            disabled={extensionOptions.length === 0 || groupComponents.length >= MAX_EXTENSIONS_PER_GROUP}
            variant="menu"
          />
        </div>
      </div>
      <div>
        <span className="extension-group-components-list__divider-info">
          Manage which extension components are included in this group
        </span>
      </div>
      <div className="extension-group-components-list__main-content">
        <div className="extension-group-components-list__main-content__table-header">
          <div className="extension-group-components-list__table-column">Name</div>
          <div className="extension-group-components-list__table-column">Type</div>
          <div className="extension-group-components-list__table-column">Display Conditions</div>
        </div>
        <div className="extension-group-components-list__main-content__list">
          {sortedGroupComponents.length === 0 ? (
            <div className="extension-group-components-list__empty">
              <p>No components in this group yet</p>
            </div>
          ) : (
            sortedGroupComponents.map((component) => (
              <div key={component.id} className="extension-group-components-list__main-content__list__row">
                <div className="extension-group-components-list__table-column extension-group-components-list__main-content__list__col">
                  <h4>
                    {component.extensionComponent?.template?.internalName ||
                      component.extensionComponent?.template?.title ||
                      '-'}
                  </h4>
                </div>
                <div className="extension-group-components-list__table-column extension-group-components-list__main-content__list__col">
                  <span>{ExtensionsTypeToDisplayName[component.extensionComponent?.type || ''] || '-'}</span>
                </div>
                <div className="extension-group-components-list__table-column extension-group-components-list__main-content__list__col">
                  {(() => {
                    const displayRules: DynamicRule[] = component.extensionComponent?.template?.displayRules || [];
                    if (!displayRules?.length) {
                      return <span>-</span>;
                    }
                    return (
                      <ExpandableText
                        title={'conditions'}
                        rows={formatExtensionConditions(displayRules)}
                        isGroups={true}
                      />
                    );
                  })()}
                </div>
                <div className="extension-group-components-list__main-content__actions">
                  <ToggleButton
                    checked={component.enabled}
                    onChange={(enabled: boolean) => handleToggleComponent(component, enabled)}
                  />
                  <ThreeDotMenu
                    className="extension-group-components-list__main-content__actions__three-dot"
                    items={getComponentActions(component)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmationModal
        title={'Are you sure?'}
        buttonText={'Remove'}
        mainContent={`You're removing the component ${removeComponent ? getComponentName(removeComponent) : ''}`}
        isOpen={removeComponent != null}
        onClose={() => {
          setRemoveComponent(null);
        }}
        onSuccess={() => handleRemoveComponent(removeComponent!)}
      />

      <PrioritizationModal<ExtensionPrioritizableItem>
        items={prioritizableItems}
        isOpen={isPriorityModalOpen}
        onClose={() => setIsPriorityModalOpen(false)}
        onSave={handleSavePriorities}
        title="Extensions Order"
        lockFirstItem={false}
        successMessage="Extension order saved"
        errorMessage="Failed to update extension order"
        renderItemDetails={(item) => (
          <span style={{ color: 'var(--on-surface-variant)', fontSize: '12px' }}>{item.type}</span>
        )}
      />
    </div>
  );
};
